import { Permission } from "@/shared/auth/permissions";
import type { ColumnMapping } from "@/shared/import/column-mapping";
import type { ImportField } from "@/shared/import/import-groups";

/**
 * The "Bank and statutory" slice of the people catalogue, claimed by the
 * shore's hr-core `bank_statutory` handler and gated on the same permission
 * and plan feature (`hr_core`) the sensitive employee profile PATCH uses. Canonical names mirror that
 * handler exactly.
 *
 * Imported bank details land UNVERIFIED: the shore never sets a verification
 * stamp from a file, so a person imported with an account cannot be paid
 * until someone confirms it. The wizard says so (`BANK_UNVERIFIED_NOTE`).
 *
 * Catalogue order matters for the loose alias pass: the account number comes
 * before the holder name, so a bare "Account" header lands on the number.
 */

const SENSITIVE = Permission.EMPLOYEE_PROFILE_EDIT_SENSITIVE_WORKSPACE;

const field = (name: string, displayName: string): ImportField => ({
  name,
  displayName,
  required: false,
  section: "bankStatutory",
  requires: SENSITIVE,
  requiresFeature: "hr_core",
});

export const BANK_STATUTORY_FIELDS: readonly ImportField[] = [
  field("salaryCurrency", "Salary currency (e.g. INR)"),
  field("salaryMode", "Salary mode"),
  field("bankName", "Bank name"),
  field("bankAccountNumber", "Bank account number"),
  field("accountHolderName", "Account holder name"),
  field("ifscCode", "IFSC code"),
  field("micrCode", "MICR code"),
  field("panNumber", "PAN"),
  field("providentFundAccount", "PF account number"),
  field("uanNumber", "UAN"),
  field("esiNumber", "ESI number"),
];

/// Matched loosely as well as exactly: long enough that containing one is a
/// real signal ("Employee Bank Account Number" is still the account number).
export const BANK_STATUTORY_ALIASES: Readonly<Record<string, readonly string[]>> = {
  salaryCurrency: ["salarycurrency", "paycurrency"],
  salaryMode: ["salarymode", "paymentmode", "modeofpayment", "salarypaymentmode"],
  bankName: ["bankname"],
  bankAccountNumber: ["bankaccountnumber", "bankaccountno", "accountnumber"],
  accountHolderName: ["accountholdername", "nameasperbank", "bankaccountholdername"],
  ifscCode: ["ifsccode", "ifsc"],
  micrCode: ["micrcode", "micr"],
  panNumber: ["pannumber", "pancardnumber"],
  providentFundAccount: ["providentfundaccount", "pfaccountnumber", "providentfundnumber"],
  uanNumber: ["uannumber"],
  esiNumber: ["esinumber", "esicnumber"],
};

/// Matched EXACTLY only. Short statutory abbreviations hide inside ordinary
/// headers: "pan" in "Company", "esi" in "Designation" and "Resignation
/// Date", "uan" in "Quantity", "pfno" in "PF Nominee". A loose match on any
/// of them would pull an unrelated column onto a sensitive field.
export const BANK_STATUTORY_EXACT_ALIASES: Readonly<Record<string, readonly string[]>> = {
  bankAccountNumber: ["accountno", "acno", "bankacno", "bankaccount"],
  accountHolderName: ["beneficiaryname", "accountholder"],
  panNumber: ["pan", "panno", "pancard"],
  providentFundAccount: ["pfaccount", "pfno", "pfnumber", "pfaccountno", "pf"],
  uanNumber: ["uan", "uanno"],
  esiNumber: ["esi", "esic", "esino", "esicno"],
};

export const BANK_UNVERIFIED_NOTE =
  "Imported bank accounts are unverified and must be confirmed before they can be paid.";

/// True when the confirmed mapping sends any bank and statutory column, which
/// is when the unverified-account note applies.
export function mapsBankStatutory(mapping: ColumnMapping | undefined): boolean {
  if (mapping === undefined) return false;
  return BANK_STATUTORY_FIELDS.some((bankField) => mapping[bankField.name] != null);
}
